import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { ChatSidebarPanel } from './ChatSidebarPanel';
import { ClassSlotList } from './ClassSlotList';
import { VideoSessionEmbed } from './VideoSessionEmbed';
import { ZoomJoinPrompt, ZoomJoinConnecting } from './ZoomJoinPrompt';
import { SessionReplayPlayer } from './SessionReplayPlayer';
import { OnlineUsers } from './OnlineUsers';
import { LiveParticipantsPanel } from './LiveParticipantsPanel';
import { ClassDiagnosticsDrawer } from './ClassDiagnosticsDrawer';
import { TasksPanel } from '../tasks/TasksPanel';
import { ResourcesPanel } from '../resources/ResourcesPanel';
import { NoticesPanel } from '../notices/NoticesPanel';
import { CourseSidebarTabs } from './CourseSidebarTabs';
import { CourseTabPanel } from './CourseTabPanel';
import { CoursePanelSheet } from './CoursePanelSheet';
import { CoursePanelTrigger } from './CoursePanelTrigger';
import { LiveJoinBanner } from './LiveJoinBanner';
import { useCompactViewport } from '../../../hooks/useCompactViewport';
import { requestZoomMediaPermissions, getMediaPermissionHint } from '../../../utils/zoomMediaPermissions';
import { leaveVideoSession } from '../../../hooks/useVideoSession';
import { useLiveClassState } from '../../../hooks/liveClassStore';
import { useZoomSessionUpdates } from '../../../hooks/useZoomSessionUpdates';
import {
    fetchCourseSlots,
    createManualClass,
    closeClassSession,
    joinClassSession,
    fetchSessionRecording,
    pollSessionRecordingReady,
    updateClassExternalLink,
} from '../../../redux/actions/zoom';
import {
    setSidebarTab,
    setVideoMode,
    leaveMeeting,
    resetZoom,
    closeReplay,
} from '../../../redux/reducers/zoom';
import { formatDateTime } from '../../../common/formatDateText';
import { buildCourseParticipants } from './buildCourseParticipants';
import './video-session.css';
import './course-live-layout.css';

const SIDEBAR_TABS = [
    { id: 'live', label: 'En vivo' },
    { id: 'chat', label: 'Chat' },
    { id: 'classes', label: 'Clases' },
    { id: 'tasks', label: 'Tareas' },
    { id: 'resources', label: 'Recursos' },
    { id: 'notices', label: 'Avisos' },
    { id: 'participants', label: 'Participantes' },
];

const RECORDING_POLL_MS = 5 * 60 * 1000;

export const CourseLiveLayout = ({
    courseId,
    deepLinkTab = null,
    openTaskId = null,
    onAccountLimit = null,
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const { courseSelected, courseListSelected } = useSelector((state) => state.course);
    const { participants: chatParticipants } = useSelector((state) => state.chatRealtime);

    const participants = useMemo(
        () => buildCourseParticipants({
            chatParticipants,
            course: courseSelected,
            students: courseListSelected,
        }),
        [chatParticipants, courseSelected, courseListSelected],
    );
    const {
        slots,
        currentLiveSession,
        latestPastSession,
        zoomConfigured,
        courseZoomEnabled,
        slotsLoaded,
        loading,
        joinCredentials,
        recordingUrl,
        recordingSessionId,
        videoMode,
        sidebarTab,
        error,
    } = useSelector((state) => state.zoom);

    const zoomEnabledForCourse = (() => {
        if (slotsLoaded && courseZoomEnabled !== undefined) {
            return courseZoomEnabled === true;
        }
        return courseSelected?.zoomEnabled === true;
    })();

    const onlineCount = participants.filter((p) => p.isOnline).length;
    // Conectados a la videollamada: distinto de los inscritos en línea.
    const liveCount = useLiveClassState((s) => s.participants.length);

    // Solo el profesor asignado al curso ve Crear sesión / Iniciar clase.
    const canCreateSessions = (() => {
        if (!user?._id) return false;

        const uid = String(user._id);
        const teacherId = courseSelected?.teacher?._id ?? courseSelected?.teacher;
        if (teacherId && String(teacherId) === uid) return true;

        // Fallback si el curso aún no trae teacher poblado: rol en participantes del chat.
        if (!teacherId) {
            const participant = participants.find((p) => String(p._id) === uid);
            return participant?.participantRole === 'profesor';
        }

        return false;
    })();

    const [pendingJoin, setPendingJoin] = useState(null);
    const [joiningWithMedia, setJoiningWithMedia] = useState(false);
    const [embedKey, setEmbedKey] = useState(0);
    const [diagnosticsSlot, setDiagnosticsSlot] = useState(null);

    const { isCompact, isLandscape } = useCompactViewport();
    const [panelOpen, setPanelOpen] = useState(false);
    const panelTriggerRef = useRef(null);

    const openPanel = useCallback(() => setPanelOpen(true), []);

    const closePanel = useCallback(() => {
        setPanelOpen(false);
        requestAnimationFrame(() => panelTriggerRef.current?.focus());
    }, []);

    // Al pasar a dos columnas la hoja ya no tiene sentido.
    useEffect(() => {
        if (!isCompact) setPanelOpen(false);
    }, [isCompact]);

    // Un enlace a una tarea o sección debe abrir el panel donde vive.
    useEffect(() => {
        if (isCompact && (deepLinkTab || openTaskId)) setPanelOpen(true);
    }, [isCompact, deepLinkTab, openTaskId]);

    useZoomSessionUpdates(courseId, {
        inMeeting: Boolean(joinCredentials),
        currentLiveSessionId: currentLiveSession?._id,
        currentUserId: user?._id,
        activeSessionId: joinCredentials?.session?._id,
        notifyLiveAvailable: !canCreateSessions,
        onRemoteSessionEnded: () => {
            setPendingJoin(null);
            setEmbedKey((k) => k + 1);
        },
    });

    const loadSlots = useCallback(() => {
        if (courseId) dispatch(fetchCourseSlots(courseId));
    }, [courseId, dispatch]);

    const handleZoomLeave = useCallback(async (opts = {}) => {
        // Embed / socket ya limpiaron la videollamada; solo refrescar UI.
        if (opts.alreadyEndedByHost || opts.closedByRemote || opts.selfLeft) {
            setPendingJoin(null);
            setEmbedKey((k) => k + 1);
            loadSlots();
            return;
        }

        await leaveVideoSession({ notify: false });
        dispatch(leaveMeeting());
        setPendingJoin(null);
        setEmbedKey((k) => k + 1);
        loadSlots();
    }, [dispatch, loadSlots]);

    const executeJoin = useCallback(async (sessionId) => {
        setJoiningWithMedia(true);
        try {
            if (joinCredentials) {
                await leaveVideoSession();
            }

            const hint = await getMediaPermissionHint();
            if (hint !== 'granted') {
                const media = await requestZoomMediaPermissions();
                if (media.warning === 'no-camera') {
                    toast('Sin cámara detectada. Te unirás solo con audio.', { icon: '⚠️' });
                } else if (media.warning === 'no-microphone') {
                    toast('Sin micrófono detectado. Podrás ver la reunión sin hablar.', { icon: '⚠️' });
                } else if (media.warning === 'no-devices') {
                    toast('No se detectó cámara ni micrófono. Intentando unirte en modo escucha…', { icon: '⚠️' });
                }
            }

            await dispatch(joinClassSession(sessionId));
            setPendingJoin(null);
        } catch (err) {
            if (err?.response?.data?.reason === 'account_limit') {
                onAccountLimit?.();
                return;
            }
            const apiMsg = err?.response?.data?.message;
            const msg = apiMsg || err?.message || 'No se pudo iniciar la videollamada';
            toast.error(msg);
            throw err;
        } finally {
            setJoiningWithMedia(false);
        }
    }, [dispatch, joinCredentials, onAccountLimit]);

    const beginJoin = useCallback(async (sessionId, label) => {
        if (joinCredentials) return;

        dispatch(setVideoMode('live'));
        setPendingJoin({ sessionId, label });
    }, [dispatch, joinCredentials]);

    const loadSlotsOnMount = useCallback(() => {
        if (courseId) dispatch(fetchCourseSlots(courseId));
    }, [courseId, dispatch]);

    useEffect(() => {
        loadSlotsOnMount();
        return () => {
            leaveVideoSession();
            dispatch(resetZoom());
        };
    }, [loadSlotsOnMount, dispatch]);

    // Solo marca modo live si hay clase en curso; nunca abre grabaciones al entrar.
    useEffect(() => {
        if (!slots.length || joinCredentials || joiningWithMedia) return;
        if (currentLiveSession) {
            dispatch(setVideoMode('live'));
        }
    }, [slots, currentLiveSession, joinCredentials, joiningWithMedia, dispatch]);

    useEffect(() => {
        if (error) toast.error(error);
    }, [error]);

    // Mientras Zoom procesa, consulta cada 5 min (sin spam de requests).
    const pendingRecordingKey = slots
        .filter((s) => s.session?._id && (
            s.recordings?.some((r) => r.pending && !r.available)
            || s.zoomCalls?.some((c) => c.recordingPending && !c.recordingReady)
            || ((s.session.recordingPending || s.recording?.pending) && !s.session.recordingReady)
        ))
        .map((s) => s.session._id)
        .sort()
        .join(',');

    useEffect(() => {
        if (!courseId || !pendingRecordingKey || joinCredentials) {
            return undefined;
        }
        const sessionIds = pendingRecordingKey.split(',').filter(Boolean);
        let cancelled = false;
        let notified = false;

        const check = async () => {
            for (const sessionId of sessionIds) {
                if (cancelled) return;
                const ready = await dispatch(pollSessionRecordingReady(sessionId, courseId));
                if (ready && !cancelled && !notified) {
                    notified = true;
                    toast.success('La grabación ya está lista. Puedes verla en Clases.');
                    return;
                }
            }
        };

        const intervalId = setInterval(check, RECORDING_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [courseId, pendingRecordingKey, joinCredentials, dispatch]);

    const confirmJoinWithPermissions = async () => {
        if (!pendingJoin?.sessionId || joiningWithMedia) return;
        try {
            await executeJoin(pendingJoin.sessionId);
        } catch {
            // toast en executeJoin
        }
    };

    const cancelPendingJoin = () => {
        setPendingJoin(null);
        if (!joinCredentials) {
            dispatch(setVideoMode('idle'));
        }
    };

    const handleCreateManual = async (payload) => {
        dispatch(setSidebarTab('classes'));
        const session = await dispatch(createManualClass(courseId, payload));
        toast.success(
            zoomEnabledForCourse
                ? 'Clase creada. Puedes iniciar Zoom cuando quieras.'
                : payload?.externalLink
                    ? 'Clase creada. Los alumnos ya pueden abrir el link.'
                    : 'Clase creada. Puedes pegar el link de Meet, Zoom, Teams o Webex cuando quieras.',
        );
        return session;
    };

    const handleEndClass = async (slot) => {
        const sessionId = slot?.session?._id;
        if (!sessionId) return;
        try {
            await dispatch(closeClassSession(sessionId));
            toast.success('Clase terminada');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo terminar la clase');
        }
    };

    const handleSaveExternalLink = async (slot, externalLink) => {
        const sessionId = slot?.session?._id;
        if (!sessionId) return false;
        try {
            await dispatch(updateClassExternalLink(sessionId, externalLink));
            toast.success(externalLink ? 'Link guardado' : 'Link eliminado');
            return true;
        } catch (err) {
            toast.error(err?.message || err?.response?.data?.message || 'No se pudo guardar el link');
            return false;
        }
    };

    const handleJoin = async (slot) => {
        if (!slot.session?._id) return;
        beginJoin(slot.session._id, `${slot.dayLabel} · ${slot.timeSlot}`);
    };

    const handleWatch = async (slot, recording = null) => {
        if (!slot.session?._id) return;
        await dispatch(fetchSessionRecording(slot.session._id, recording?._id));
    };

    const showCost = user?.typeUser === 'director';
    // El backend exige director o profesor del curso para la bitácora de conexión.
    const canSeeDiagnostics = showCost || canCreateSessions;

    const handleOpenDiagnostics = (slot) => {
        if (!slot?.session?._id) return;
        setDiagnosticsSlot({
            sessionId: slot.session._id,
            title: slot.topic || slot.session.topic || slot.dayLabel || 'Clase',
        });
    };

    const handleCloseReplay = () => {
        dispatch(closeReplay());
    };

    const handleJoinLive = async () => {
        if (!currentLiveSession?._id) return;
        beginJoin(
            currentLiveSession._id,
            currentLiveSession.topic
                || (currentLiveSession.isTest ? 'Sesión en vivo' : 'Clase en vivo'),
        );
    };

    const activeReplaySlot = slots.find((s) => s.session?._id === recordingSessionId);

    const inCall = zoomEnabledForCourse && Boolean(joinCredentials && videoMode === 'live');
    const watchingReplay = Boolean(
        zoomEnabledForCourse && recordingUrl && videoMode === 'replay',
    );
    // En vivo o viendo grabación: panel lateral con Chat + resto de tabs.
    const inSessionView = inCall || watchingReplay;
    const initialTabRef = useRef(deepLinkTab);

    /**
     * Fuera de sesión el chat va en el panel principal y «En vivo» no tiene a
     * quién mostrar: en ambos casos hay que caer a un tab que siga existiendo.
     */
    useEffect(() => {
        const unavailable = (!inSessionView && sidebarTab === 'chat')
            || (!inCall && sidebarTab === 'live');
        if (!unavailable) return;

        const fallback = initialTabRef.current;
        const target = fallback && fallback !== 'chat' && fallback !== 'live'
            ? fallback
            : 'classes';
        initialTabRef.current = null;
        dispatch(setSidebarTab(target));
    }, [inSessionView, inCall, sidebarTab, dispatch]);

    const renderTabContent = () => {
        if (sidebarTab === 'classes') {
            return (
                <ClassSlotList
                    slots={slots}
                    latestPastSession={latestPastSession}
                    canCreate={canCreateSessions}
                    onCreateManual={handleCreateManual}
                    onEndClass={handleEndClass}
                    onJoin={handleJoin}
                    onWatch={handleWatch}
                    onSaveExternalLink={handleSaveExternalLink}
                    onOpenDiagnostics={handleOpenDiagnostics}
                    canSeeDiagnostics={canSeeDiagnostics}
                    loading={loading}
                    slotsLoaded={slotsLoaded}
                    zoomConfigured={zoomConfigured}
                    zoomEnabled={zoomEnabledForCourse}
                    showCost={showCost}
                    joinedSessionId={joinCredentials?.session?._id || null}
                />
            );
        }
        if (sidebarTab === 'tasks') {
            return <TasksPanel courseId={courseId} initialTaskId={openTaskId} />;
        }
        if (sidebarTab === 'resources') {
            return <ResourcesPanel courseId={courseId} />;
        }
        if (sidebarTab === 'notices') {
            return <NoticesPanel courseId={courseId} />;
        }
        if (sidebarTab === 'participants') {
            return <OnlineUsers participants={participants} currentUserId={user?._id} />;
        }
        if (sidebarTab === 'live') {
            return <LiveParticipantsPanel />;
        }
        return null;
    };

    const renderLiveBanner = () => {
        if (!zoomEnabledForCourse) return null;
        if (!currentLiveSession || joinCredentials || joiningWithMedia) return null;
        return (
            <LiveJoinBanner
                title={
                    currentLiveSession.topic
                    || (currentLiveSession.isTest ? 'Sesión en vivo' : 'Clase en vivo disponible')
                }
                asHost={canCreateSessions}
                onJoin={handleJoinLive}
            />
        );
    };

    const renderMainPanel = (headerAction = null) => {
        if (inCall) {
            return (
                <VideoSessionEmbed
                    key={embedKey}
                    credentials={joinCredentials}
                    courseId={courseId}
                    onLeave={handleZoomLeave}
                    onOpenSidePanel={isCompact ? openPanel : null}
                />
            );
        }

        if (watchingReplay) {
            return (
                <SessionReplayPlayer
                    playUrl={recordingUrl}
                    title={activeReplaySlot
                        ? (activeReplaySlot.topic || activeReplaySlot.session?.topic || activeReplaySlot.dayLabel)
                            + (activeReplaySlot.scheduledStart
                                ? ` · ${formatDateTime(activeReplaySlot.scheduledStart)}`
                                : '')
                        : 'Clase grabada'}
                    onClose={handleCloseReplay}
                    headerAction={headerAction}
                />
            );
        }

        if (joiningWithMedia) {
            return (
                <div className="flex-1 min-h-0">
                    <ZoomJoinConnecting />
                </div>
            );
        }

        if (pendingJoin) {
            return (
                <div className="flex-1 min-h-0">
                    <ZoomJoinPrompt
                        subtitle={pendingJoin.label}
                        loading={joiningWithMedia}
                        onConfirm={confirmJoinWithPermissions}
                        onCancel={cancelPendingJoin}
                    />
                </div>
            );
        }

        return <ChatSidebarPanel courseId={courseId} headerAction={headerAction} />;
    };

    const panelTabs = SIDEBAR_TABS.filter((tab) => {
        if (tab.id === 'chat') return inSessionView;
        if (tab.id === 'live') return inCall;
        return true;
    });
    const handleTabChange = (tabId) => dispatch(setSidebarTab(tabId));

    // El chat vive en el panel solo durante la sesión; fuera de ella ocupa el área principal.
    const panelContent = (
        <CourseTabPanel tabId={sidebarTab} tabs={panelTabs}>
            {inSessionView && sidebarTab === 'chat'
                ? <ChatSidebarPanel courseId={courseId} />
                : renderTabContent()}
        </CourseTabPanel>
    );

    const diagnosticsDrawer = canSeeDiagnostics ? (
        <ClassDiagnosticsDrawer
            isOpen={Boolean(diagnosticsSlot)}
            sessionId={diagnosticsSlot?.sessionId || null}
            title={diagnosticsSlot?.title || ''}
            onClose={() => setDiagnosticsSlot(null)}
        />
    ) : null;

    if (isCompact) {
        /**
         * En videollamada el panel se abre desde los botones Chat y Gente de la
         * barra de la llamada; en el resto de vistas va en la barra superior.
         */
        const panelTrigger = (
            <CoursePanelTrigger
                ref={panelTriggerRef}
                onClick={openPanel}
                onlineCount={onlineCount}
                expanded={panelOpen}
                controls="course-panel-sheet"
            />
        );

        return (
            <div className="course-live-shell course-live-shell--compact flex h-full w-full min-h-0 bg-gray-100 border-t border-gray-200">
                <div className="course-live-shell__main">
                    {!inSessionView ? renderLiveBanner() : null}
                    {renderMainPanel(panelTrigger)}
                </div>

                <CoursePanelSheet
                    open={panelOpen}
                    onClose={closePanel}
                    isLandscape={isLandscape}
                    tabs={panelTabs}
                    activeTab={sidebarTab}
                    onTabChange={handleTabChange}
                    onlineCount={onlineCount}
                    liveCount={liveCount}
                    showLiveOnClasses={Boolean(currentLiveSession)}
                >
                    {panelContent}
                </CoursePanelSheet>

                {diagnosticsDrawer}
            </div>
        );
    }

    return (
        <div className="course-live-shell flex flex-col h-full w-full min-h-0 bg-gray-100 border-t border-gray-200">
            {!inSessionView ? renderLiveBanner() : null}
            <div className="course-live-shell__body">
                <div className="flex-[67] min-w-0 min-h-0 flex flex-col relative bg-white">
                    {renderMainPanel()}
                </div>

                <div className="flex-[33] min-w-[280px] flex flex-col min-h-0 flex-shrink-0 bg-white border-l border-gray-200">
                    <CourseSidebarTabs
                        tabs={panelTabs}
                        activeTab={sidebarTab}
                        onTabChange={handleTabChange}
                        onlineCount={onlineCount}
                        liveCount={liveCount}
                        showLiveOnClasses={Boolean(currentLiveSession)}
                    />

                    <div className="flex-1 min-h-0 overflow-hidden">
                        {panelContent}
                    </div>
                </div>
            </div>

            {diagnosticsDrawer}
        </div>
    );
};
